import { z } from 'zod';

import { CatalystTagSchema, type CatalystTag } from './enums';

/** Defensive ceiling: the wire list is otherwise unbounded (plan §6.5a). */
export const CATALYSTS_MAX = 32;

/**
 * Live calendar tags only. ANALYST / SECTOR stay synthetic — they are news
 * copy, not dated Yahoo events (ADR-0039).
 */
export const CALENDAR_EVENT_TAGS = [
  'EARNINGS',
  'CAPITAL',
] as const satisfies readonly CatalystTag[];
export const CalendarEventTagSchema = z.enum(CALENDAR_EVENT_TAGS);
export type CalendarEventTag = z.infer<typeof CalendarEventTagSchema>;

/**
 * Factual rail labels. Not vendor prose, not EPS estimates, not quarter
 * templates — the date field already carries the day.
 */
export const CALENDAR_EVENT_HEADLINE = {
  EARNINGS: 'Earnings date',
  CAPITAL: 'Ex-dividend',
} as const satisfies Readonly<Record<CalendarEventTag, string>>;

/**
 * Catalyst / news item (F15). Synthetic mode still seeds four headlines.
 * Live mode emits dated Yahoo events or `[]` on a miss (ADR-0039).
 */
export const CatalystSchema = z.object({
  /* Display string: synthetic demo-fidelity uses `MON D YYYY`; live Yahoo
     rows are ISO `YYYY-MM-DD` (the DATE). The F15 list formats ISO to the
     month-name form; other strings pass through. */
  date: z.string().min(1).max(64),
  tag: CatalystTagSchema,
  headline: z.string().min(1).max(500),
});
export type Catalyst = z.infer<typeof CatalystSchema>;

/** Capped at CATALYSTS_MAX — see the constant for the rationale. */
export const CatalystsSchema = z.array(CatalystSchema).max(CATALYSTS_MAX);
export type Catalysts = z.infer<typeof CatalystsSchema>;
